import { isObject } from "lodash-es";

import { Provider, Resource } from "src/api/connectApi/types";
import {
  DecisionHistoryIntegrationDuration,
  DecisionsOutcomeFilter,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { EnumOptionsBE, GenericObject } from "src/api/flowTypes";
import { S3PreSignedUrlData } from "src/api/s3";
import {
  FlowMeta,
  FlowReviewConfiguration,
  FlowTestRunConfiguration,
  TypingInfo,
  WorkspaceDataplane,
  WorkspaceSettings,
} from "src/clients/flow-api";
import { MLNodeMetadata } from "src/constants/NodeDataTypes";
import { JSONValue } from "src/datasets/DatasetTable/types";
import { JobRun } from "src/jobs/types";

export type FieldErrors = { [id: string]: string };
export type FieldErrorsBE = {
  field_id: string;
  field_name: string;
  message: string;
}[];

export enum GraphRunResultType {
  SUCCESS = "success",
  ERROR = "error",
  JOB_IN_PROGRESS = "job_in_progress",
}

export type AuxDataEntry = Record<string, unknown>;

export type DataRow<T = unknown> = Record<string, T>;

export type NodeExecutionMetadata = {
  [nodeID: string]: {
    variables?: {
      [key: string]: { value: JSONValue; type_: string | null | undefined };
    };
    accessed_fields?: string[];
    updated_fields?: string[];
    data_source?: "empty_response" | "mock_data" | "connect";
    agent_session_id?: string;
  } | null;
};

export type IndexedDataRow = {
  index: number;
  data: DataRow;
  aux_data: DataRow;
  node_execution_metadata?: NodeExecutionMetadata;
  /**
   * Only available for Flow nodes or Loop nodes. The data provided to the child flow.
   */
  flow_nodes_inputs?: DataRow | DataRow[];
  expected_output_data?: DataRow;
  expected_output_keys_mismatch?: string[];
};

export type ErrorBaseInfo = {
  msg: string | GenericObject | React.ReactNode;
  node_id?: string;
  status_code?: number;
  loc_type?: "field" | "line" | "node";
  loc?: string | number;
  field_errors?: FieldErrorsBE;
  integration_durations?: DecisionHistoryIntegrationDuration[];
  exc_type:
    | "parse"
    | "compile"
    | "runtime"
    | "runtime_http_error"
    | "data_integration_error"
    | "row_ignored"
    | "unknown_error";
};

export type DecisionHistoryError = ErrorBaseInfo & {
  data?: Record<string, unknown>;
  aux_data?: AuxDataEntry;
  params: Record<string, unknown>;
};

export type ErroredRow = IndexedDataRow &
  ErrorBaseInfo & {
    error_hash: string;
    provenance?: Record<string, GenericObject>;
  };

export type IgnoredRow = IndexedDataRow &
  ErrorBaseInfo & {
    provenance?: Record<string, GenericObject>;
  };

export type ErroredRowGroup = {
  failure_count: number;
  example_failure: ErroredRow;
  error_hash: string;
};

export type NodeTestRunResult = {
  success_count: number;
  failure_count: number;
  ignored_count: number;
  errors_count: number;
  total_count: number;
  expected_output_mismatch_count?: number;
  data_columns: string[];
  aux_data_columns: string[];
  expected_output_columns?: string[];
  success_pages_urls: string[];
  failure_pages_urls: string[];
  ignored_pages_urls: string[];
  errors_pages_urls: string[];
  success_match_pages_urls: string[];
  success_mismatch_pages_urls: string[];
  node_id: string;
  version_id: string;
  test_run_id: string;
  typing: TypingInfo | null; // Before node is run
  node_results_typing: TypingInfo | null; // After node is run
};

// As this type might diverge from the other uses of ErrorBaseInfo we already split it semantically
// eslint-disable-next-line sonarjs/redundant-type-aliases
export type GraphLevelTestError = ErrorBaseInfo;

export type FlowRunnerResultV2 =
  | {
      type: GraphRunResultType.SUCCESS;
      nodes: { [nodeId: string]: NodeTestRunResult };
    }
  | {
      type: GraphRunResultType.ERROR;
      error: GraphLevelTestError;
      errors?: GraphLevelTestError[];
    };

export type JobRunnerResult =
  | {
      type: GraphRunResultType.SUCCESS;
      jobRun: JobRun;
      datasetId: string;
    }
  | {
      type: GraphRunResultType.JOB_IN_PROGRESS;
      jobRun: JobRun;
      datasetId: string;
    };

export type FlowRunnerAsyncRunResult = {
  run_id: string;
};

export type FlowAnalyzeParametersResultBE = { [nodeId: string]: string[] };

export enum DecisionEnvironment {
  LIVE = "live",
  SANDBOX = "sandbox",
}
export enum DecideDecisionStatus {
  SUCCESS = "success",
  ERROR = "error",
  PENDING = "pending",
}

type DecisionMetadata = {
  job_id?: string;
  job_run_id?: string;
  version?: string;
  entity_id?: string;
};

export type DecideDecisionSuccess<ResponseData extends GenericObject> = {
  data: ResponseData;
  status: DecideDecisionStatus.SUCCESS;
  metadata: DecisionMetadata;
  raw_provider_responses: unknown;
};
export type DecideDecisionError = {
  detail: ErrorBaseInfo;
  status: DecideDecisionStatus.ERROR;
  metadata: DecisionMetadata;
  raw_provider_responses: unknown;
};
/**
 * Use to check whether catched errors are DecideDecisionErrors
 */
export const isDecideDecisionError = (
  error: unknown,
): error is DecideDecisionError => {
  return (
    isObject(error) &&
    "status" in error &&
    error.status === DecideDecisionStatus.ERROR &&
    "detail" in error &&
    isObject(error.detail) &&
    "exc_type" in error.detail &&
    typeof error.detail.exc_type === "string" &&
    "msg" in error.detail &&
    typeof error.detail.msg === "string"
  );
};

type FlowAnalyzeErrorBE = {
  type: "error";
};

type FlowAnalyzeSuccessBE = {
  type: "success";
  params: FlowAnalyzeParametersResultBE;
};

export type FlowAnalyzeResultBE = FlowAnalyzeErrorBE | FlowAnalyzeSuccessBE;

export type FlowCreate = {
  name: string;
  description: string;
  wsId: string;
  slug: string;
  flow_folder_id?: string | null;
  initial_version_description: string;
  initial_version_name: string;
  created_by_id: string;
  review_configuration?: FlowReviewConfiguration;
  test_run_configuration?: FlowTestRunConfiguration;
};

export type FlowUpdate = {
  flow_folder_id?: string | null;
  flowId: string;
  name?: string;
  meta?: FlowMeta;
  default_version?: string;
  default_sandbox_version?: string;
  chart_order?: string[];
  review_configuration?: FlowReviewConfiguration;
  test_run_configuration?: FlowTestRunConfiguration;
  ui_default_decision_fields_exclude?: string[];
  ui_default_inputs_include?: string[];
  ui_default_outputs_include?: string[];
  ui_default_outcomes_include?: string[];
} & (
  | {
      // When moving the flow to a new folder, both flow_folder_id and old_folder_id are required
      flow_folder_id: string | null;
      old_folder_id: string | null;
    }
  | {
      flow_folder_id?: never;
      old_folder_id?: never;
    }
);

export type FlowVersionCreate = {
  flowId: string;
  name: string;
  release_note: string;
  created_by_id: string;
};

export type FlowVersionDuplicate = {
  flowVersionId: string;
  name: string;
  release_note: string;
  created_by_id: string;
  include_comments: boolean;
  targetFlowId?: string;
};

export type UploadModelData = {
  baseUrl: string;
  flowSlug: string;
  file: File;
};

export type UploadModelSignedUrlData = {
  model_id: string;
} & S3PreSignedUrlData;

export type ParsedModelDataPending = {
  type: "pending";
};

export type ParsedModelDataSuccess = {
  type: "success";
  model_id: string;
  metadata: MLNodeMetadata;
};

export type ParsedModelDataError = {
  type: "error";
  model_id: string;
  error: { [key: string]: string };
};

export type ParsedModelDataBE = {
  result:
    | ParsedModelDataPending
    | ParsedModelDataSuccess
    | ParsedModelDataError;
};

export type SummaryBE = {
  decisions: number;
  errors: number;
  latency_p90: number;
};

export type MetricsSummaryResponse = {
  current_period: SummaryBE;
  previous_period: SummaryBE;
};

type MetricsDecisionBin = {
  bin: string;
  value: number;
  version_id: string;
};

export type MetricsDecisionsResponse = {
  by_version: MetricsDecisionBin[];
};

type MetricsBin = {
  bin: string;
  value: number;
};

type MetricsErrorBinWithCode = MetricsBin & {
  code: string;
};

export type MetricsErrorsResponse = {
  by_error: MetricsErrorBinWithCode[];
  error_rate: MetricsBin[];
};

type MetricsLatencyBin = MetricsBin & {
  version_id: string;
};

export type MetricsLatencyResponse = {
  by_version: MetricsLatencyBin[];
  total: MetricsBin[];
};

export type DecisionSummaryResponse = {
  groups: {
    flow?: {
      id: string;
    };
    count?: number;
  }[];
};

export type MetricsNodeStatusCounts = {
  success: number;
  failed: number;
  pending: number;
  cancelled: number;
  ignored: number;
  other: number;
};

export type MetricsNodeStatusCountsResponse = {
  status_counts: Record<string, MetricsNodeStatusCounts>;
};

export const GLOBAL_FEATURES_TYPE = "features";

export const DesiredTypes = [
  "string",
  "boolean",
  "number",
  "integer",
  "object",
  "array",
  "datetime",
  "date",
  "any",
  "enum",
  "files",
] as const;
export type DesiredType = (typeof DesiredTypes)[number];

export const DesiredTypeValues: Record<DesiredType, DesiredType> =
  DesiredTypes.reduce(
    (acc, type) => {
      acc[type] = type;
      return acc;
    },
    {} as Record<DesiredType, DesiredType>,
  );

export type DatasetColumn = {
  name: string;
  desired_type: DesiredType;
  use_subflow_mocks: boolean;
};

export type DatasetRow = {
  id: string;
} & DatasetRowData;

type DatasetRowData = {
  input_data: Record<string, unknown>;
  mock_data?: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  outcome_data?: Record<string, unknown>;
};

export type DatasetRowDataGroup = keyof DatasetRowData;

export type DatasetPurpose = "test_run" | "job_source" | "simulation";

export type SimulationDatasetDetail = {
  size_bytes: number;
  dataset_job_id: string;
  s3_bucket_dataset_id?: string;
  job_source_id?: string;
};

export type Dataset = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  flow_id: string;
  etag: string;
  source: "file" | "history" | "scratch";
  purpose: DatasetPurpose;
  created_by_id?: string;
  updated_by_id?: string;
  row_count?: number;
  simulation_dataset_detail?: SimulationDatasetDetail;
} & {
  [k in DatasetColumnGroup]: DatasetColumn[];
};

export type DatasetColumnGroup =
  | "input_columns"
  | "mock_columns"
  | "output_columns"
  | "outcome_columns";

export const DatasetColumnGroupValues: Record<
  DatasetColumnGroup,
  DatasetColumnGroup
> = {
  input_columns: "input_columns",
  mock_columns: "mock_columns",
  output_columns: "output_columns",
  outcome_columns: "outcome_columns",
} as const;

export type ExtendedDatasetColumnGroups =
  | DatasetColumnGroup
  | "additional_columns";

export const DatasetColumnGroupToRowDataGroupMap = {
  input_columns: "input_data",
  mock_columns: "mock_data",
  output_columns: "output_data",
  outcome_columns: "outcome_data",
} as const satisfies Record<DatasetColumnGroup, DatasetRowDataGroup>;

export const DatasetRowDataGroupToColumnGroupMap = {
  input_data: "input_columns",
  mock_data: "mock_columns",
  output_data: "output_columns",
  outcome_data: "outcome_columns",
} as const satisfies Record<DatasetRowDataGroup, DatasetColumnGroup>;

export type DatasetResponse = {
  dataset: Dataset;
  from_: number;
  size: number;
  total: number;
  rows: DatasetRow[];
};

export type DatasetPage = Pick<
  DatasetResponse,
  "from_" | "rows" | "size" | "total"
> & { fetchedAt: number };

export type DatasetFileUploadStatus =
  | "PENDING"
  | "COMPLETED"
  | "FAILED"
  | "PARTIALLY_COMPLETED";

export type DatasetFileUpload = {
  flow_id: string;
  dataset_id: string;
  file_name: string;
  created_by_id: string;
  s3_presigned_url: string;
  s3_presigned_fields: Record<string, string>;
  id: string;
  status: DatasetFileUploadStatus;
  error_metadata: string;
  created_at: string;
  updated_at: string;
};

export type DatasetFileFormat = "csv" | "json" | "xlsx";

type ReviewCaseDecision = {
  id: string;
  entity_id: Nullable<string>;
  start_time: string;
  is_sandbox: boolean;
};

type ReviewCaseFlowMetadata = {
  id: string;
  slug: string;
  version_id: string;
  version_name: string;
  version_etag: string;
};

export type ReviewCaseResponseSchemaProperty = { description: string } & (
  | {
      type: "integer" | "number" | "boolean";
    }
  | {
      type: "string";
      $comment?: string;
    }
  | {
      type: "array";
      items: { type: "string" };
      $comment?: string;
    }
  | { enum: EnumOptionsBE }
);

export type ReviewCaseResponseSchema = {
  $schema?: string;
  type: "object";
  properties: Record<string, ReviewCaseResponseSchemaProperty>;
  required: string[];
  order?: string[];
  description: string;
};

export type ReviewCaseResponseData = Record<
  string,
  string | number | boolean | string[] | undefined
>;

export type ReviewCaseResponse = {
  schema: ReviewCaseResponseSchema;
  data: Nullable<ReviewCaseResponseData>;
};

export type ReviewCaseStatusPending = "needs_review" | "in_progress";
export type ReviewCaseStatus = ReviewCaseStatusPending | "completed";

export type ReviewCaseHighlightsDivider = { type: "divider" };

export type ReviewCaseHighlightsDescription = {
  type: "description";
  value: string;
};

export type ReviewCaseHighlightsField = {
  type: "field";
  value: JSONValue;
  readable_name: string;
};

export type ReviewCaseHighlightsFile = {
  type: "file";
  value: string;
};

export type ReviewCaseHighlightsElement =
  | ReviewCaseHighlightsDivider
  | ReviewCaseHighlightsDescription
  | ReviewCaseHighlightsField
  | ReviewCaseHighlightsFile;

export type ReviewCase = {
  id: string;
  number: number;
  created_at: string;
  status: ReviewCaseStatus;
  assignee: string | null;
  node_name: string;
  response: ReviewCaseResponse;
  flow: ReviewCaseFlowMetadata;
  decision: ReviewCaseDecision;
  highlights: ReviewCaseHighlightsElement[];
  connection_id: string;
  etag: string;
  updated_by: Nullable<string>;
  updated_at: string;
};

export type ReviewCaseExternalReport = {
  provider: Provider;
  resource: Resource;
  connection_name?: string;
  node_name: string;
  response: GenericObject;
};

export type ReviewCaseDataAvailability = "pending" | "available";

export type ReviewCaseWithDecisionData = ReviewCase & {
  /* This data will be there eventually
   but it can be undefined if the
  decision processing is delayed */
  inspect_data?: GenericObject;
  external_data?: ReviewCaseExternalReport[];
  data_availability?: ReviewCaseDataAvailability;
};

export type ListReviewCasesResponse = {
  cases: ReviewCase[];

  page_min_timestamp: Nullable<string>;
  page_max_timestamp: Nullable<string>;
};

export type ReviewCasesAvailableFiltersResponse = {
  assignees: string[];
  node_names: string[];
  flow_versions: string[];
};

export type ReviewSummaryResponse = {
  open_count: number;
  open_and_assigned_to_me_count: number;
  in_progress_count: number;
  long_time_open_count: number;
  completed_recently_count: number;
};

export type DuplicateDatasetJobRequest = {
  source_dataset_id: string;
  new_dataset_name?: string;
  row_limit?: number;
};

export type DownloadDatasetJobRequest = {
  dataset_id: string;
  format: DatasetFileFormat;
};

export type EventUploadDatasetJobRequest = {
  event_type: string;
  environment: "live" | "sandbox";
  file_name: string;
};

export type SimulationFailuresDatasetJobRequest = {
  simulation_job_run_id: string;
  name: string;
};

export type SimulationDownloadDatasetJobRequest = {
  simulation_job_run_id: string;
  format: "csv" | "ndjson";
};

export type HistoryExportDatasetJobRequest = {
  flow_slug: string;
  input_filters: {
    end_date?: string;
    entity_id?: string;
    flow_versions?: string[];
    is_sandbox?: boolean;
    job_id?: string;
    job_run_id?: string;
    start_date?: string;
    status_codes?: string[];
    traffic_policy_id?: string;
  };
  output_filters: {
    include_extra_fields: boolean;
    include_response_data: boolean;
    include_traffic_policy: boolean;
    include_request_data: boolean;
  };
  format: DatasetFileFormat;
};

export type ExportDecisionsDatasetJobRequest = {
  format: "csv" | "json";
  flow_slug: string;
  start_date: string;
  end_date: string;
  flow_versions: string[];
  status_codes: string[];
  traffic_policy_id?: string;
  sub_sampling_size?: number;
  outcome_filters?: DecisionsOutcomeFilter[];
};

// Make settings non-optional
export type WorkspaceWithSettings = Omit<WorkspaceDataplane, "settings"> & {
  settings: WorkspaceSettings;
};

export type OutcomeReport = {
  created_at: string;
  decision_id: string;
  entity_id: string;
  flow_id: string;
  id: string;
  outcome_type_id: string;
  outcome_type_key: string;
  payload: Record<string, unknown>;
};
